<template>
    <div
        class="flex flex-col max-w-screen-xl mx-auto px-4 sm:px-6 md:px-8 lg:px-32">
        <!-- Hero -->
        <div class="flex flex-col items-center">
            <div
                class="text-lg sm:text-xl md:text-4xl text-san-marino-600 font-semibold mt-8 text-center px-8 sm:px-36 md:px-16 lg:px-20 xl:px-64">
                <h1>Nereye seyahat ederseniz edin Kolay eSIM ile bağlı kalın</h1>
            </div>
            <div
                class="text-lg sm:text-xl md:text-3xl text-san-marino-800 font-semibold mt-2 text-center px-4">
                <h2>Uygun fiyatlarla kesintisiz internet erişimi</h2>
            </div>
            <AutoComplete
                v-model="selectedCountry"
                :suggestions="items"
                @complete="search"
                forceSelection
                inputClass="!rounded-full mt-6 w-full bg-san-marino-100 text-san-marino-700 border-san-marino-700"
                placeholder="&#128269;    200'dan fazla ülke ve bölgede geçerli veri paketlerini arayın"
                pt:root="w-[calc(100%-2rem)] sm:w-3/4 md:w-2/3 lg:w-1/2 mb-20"
                emptySearchMessage="Her hangi bir sonuç bulunamadı"
                optionLabel="name"
                @option-select="navigateToCountry()">
                <template #option="slotProps">
                    <div class="flex items-center">
                        <span
                            class="!w-6 sm:!w-8 h-6 sm:h-8 fi drop-shadow"
                            :class="`fi-${slotProps.option.code.toLowerCase()}`"></span>
                        <div class="ml-3 sm:ml-4">
                            {{ slotProps.option.name }}
                        </div>
                    </div>
                </template>
            </AutoComplete>
        </div>
        <!-- Packages Tab List -->
        <div class="card">
            <Tabs value="0">
                <TabList pt:tabList="justify-center">
                    <Tab value="0" pt:root="px-1 sm:px-4"
                        >Yerel eSIM'ler</Tab
                    >
                    <Tab value="1" pt:root="px-1 sm:px-4"
                        >Bölgesel eSIM'ler</Tab
                    >
                    <Tab value="2" pt:root="px-1 sm:px-4"
                        >Global eSIM'ler</Tab
                    >
                </TabList>
                <TabPanels pt:root="px-0">
                    <TabPanel value="0">
                        <!-- Popular Countries -->
                        <div class="flex flex-col items-center">
                            <span
                                class="text-xl sm:text-2xl text-san-marino-500 font-bold mb-6 sm:mb-8 mt-4"
                                >{{ isHiddenAllCountries ? 'Popüler Ülkeler' : 'Tüm Ülkeler' }}</span
                            >
                            <div
                                class="flex flex-wrap w-full gap-3 sm:gap-4 md:gap-5 lg:gap-6 xl:gap-8 justify-center">
                                <div
                                    v-ripple
                                    v-for="country in popularCountries"
                                    class="flex w-full sm:w-[calc(50%-8px)] md:w-[calc(33.33%-12px)] lg:w-60 xl:w-80 h-14 sm:h-16 bg-san-marino-100 drop-shadow px-3 sm:px-4">
                                    <NuxtLink
                                        class="flex items-center gap-x-2 w-full"
                                        :to="`/${country.slug}`">
                                        <span
                                            class="!w-8 sm:!w-12 h-6 sm:h-8 fi drop-shadow"
                                            :class="`fi-${country.code.toLowerCase()}`"></span>
                                        <span
                                            class="text-san-marino-700 text-lg sm:text-xl font-medium flex-1 truncate"
                                            >{{ country.name }}</span
                                        >
                                        <span
                                            class="pi pi-chevron-right text-san-marino-700"></span>
                                    </NuxtLink>
                                </div>
                                <div
                                    v-if="!isHiddenAllCountries"
                                    v-ripple
                                    v-for="country in unpopularCountries"
                                    class="flex w-full sm:w-[calc(50%-8px)] md:w-[calc(33.33%-12px)] lg:w-60 xl:w-80 h-14 sm:h-16 bg-san-marino-100 drop-shadow px-3 sm:px-4">
                                    <NuxtLink
                                        class="flex items-center gap-x-2 w-full"
                                        :to="`/${country.slug}`">
                                        <span
                                            class="!w-8 sm:!w-12 h-6 sm:h-8 fi drop-shadow"
                                            :class="`fi-${country.code.toLowerCase()}`"></span>
                                        <span
                                            class="text-san-marino-700 text-lg sm:text-xl font-medium flex-1 truncate"
                                            >{{ country.name }}</span
                                        >
                                        <span
                                            class="pi pi-chevron-right text-san-marino-700"></span>
                                    </NuxtLink>
                                </div>
                            </div>
                            <Button
                                :label="isHiddenAllCountries ? 'Bütün Ülkeleri Göster' : 'Popüler Ülkeleri Göster'"
                                class="text-xl font-medium my-6 sm:my-8"
                                @click="isHiddenAllCountries = !isHiddenAllCountries" />
                        </div>
                    </TabPanel>
                    <TabPanel value="1">
                        <!-- Regions -->
                        <div class="flex flex-col items-center">
                            <div
                                class="flex flex-wrap w-full gap-3 sm:gap-4 md:gap-5 lg:gap-6 xl:gap-8 justify-center my-8">
                                <div
                                    v-ripple
                                    v-for="region in regions"
                                    class="flex w-full sm:w-[calc(50%-8px)] md:w-[calc(33.33%-12px)] lg:w-60 xl:w-80 h-14 sm:h-16 bg-san-marino-100 drop-shadow px-3 sm:px-4">
                                    <NuxtLink
                                        class="flex items-center gap-x-2 w-full"
                                        :to="`/${region.slug}`">
                                        <span
                                            class="text-san-marino-700 text-lg sm:text-xl font-medium flex-1 truncate"
                                            >{{ region.name }}</span
                                        >
                                        <span
                                            class="pi pi-chevron-right text-san-marino-700"></span>
                                    </NuxtLink>
                                </div>
                            </div>
                        </div>
                    </TabPanel>
                    <TabPanel value="2">
                        <!-- Global -->
                        <div class="flex flex-col items-center">
                            <div
                                class="flex flex-wrap w-full gap-3 sm:gap-4 md:gap-5 lg:gap-6 xl:gap-8 justify-center my-8">
                                <div
                                    v-ripple
                                    class="flex w-full sm:w-[calc(50%-8px)] md:w-[calc(33.33%-12px)] lg:w-60 xl:w-80 h-14 sm:h-16 bg-san-marino-100 drop-shadow px-3 sm:px-4">
                                    <NuxtLink
                                        class="flex items-center gap-x-2 w-full"
                                        :to="`/world-esim`">
                                        <span
                                            class="text-san-marino-700 text-lg sm:text-xl font-medium flex-1 truncate"
                                            >{{ countries.find((c) => c.code === 'world').name }}</span
                                        >
                                        <span
                                            class="pi pi-chevron-right text-san-marino-700"></span>
                                    </NuxtLink>
                                </div>
                            </div>
                        </div>
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </div>
        <!-- How to works -->
        <HowToWorks />
        <!-- Compatible Devices -->
        <CompatibleDevices />
        <!-- Frequently Asked Questions -->
        <FrequentlyAskedQuestions />
    </div>
</template>

<script setup>
import { ref } from "vue";

useHead({
    title: "Uluslararası seyahatler için eSIM satın alın",
    meta: [
        {
            name: "description",
            content:
                "200'den fazla seyahat noktasında kesintisiz ve hızlı internet için uygun fiyatlarla sunulan seyahat eSIM'lerini bulun. Kolay eSIM ile bağlantıda kalın.",
        },
        {
            name: "keywords",
            content:
                "eSIM, eSIM alma, bölgesel eSIM, yerel eSIM, bölgesel eSIM alma, yerel eSIM alma, yerel eSIM satın alma, bölgesel eSIM satın alma, veri eSIM, e-SIM, eSIM planı, eSIM mağazası, eSIM satış yeri, seyahat eden, gezgin, veri planı, e-SIM alma, ucuz veri, bağlantı",
        },
    ],
});

const client = useSanctumClient();
const router = useRouter();

const selectedCountry = ref("");
const items = ref([]);
const isHiddenAllCountries = ref(true);

const {
    data: countries,
    status,
    error,
    refresh,
    clear
} = await useAsyncData(
    "countries",
    () => client("/countries")
);

const popularCountries = ref([
    { code: "TR" },
    { code: "GR" },
    { code: "DE" },
    { code: "IT" },
    { code: "ES" },
    { code: "FR" },
    { code: "GB" },
    { code: "NL" },
    { code: "US" },
    { code: "PT" },
    { code: "JP" },
    { code: "AE" },
    { code: "EG" },
    { code: "AT" },
    { code: "TH" },
]);

const unpopularCountries = ref([]);
const regions = ref([]);

popularCountries.value.forEach((country) => {
    country.name = countries.value.find((c) => c.code === country.code).name;
    country.slug = countries.value.find((c) => c.code === country.code).slug;
});

unpopularCountries.value = countries.value
    .filter(
        (country) =>
            !popularCountries.value.some((c) => c.code === country.code)
    )
    .filter((country) => country.area === "local");

regions.value = countries.value
    .filter((country) => country.area === "global")
    .filter((country) => country.code !== "world");

const search = (event) => {
    items.value = countries.value.filter((country) =>
        country.name?.toLowerCase().includes(event.query.toLowerCase())
    );
};

const navigateToCountry = () => {
    if (selectedCountry.value) {
        const path = selectedCountry.value.slug;

        router.push(`/${path}`);
    }
};
</script>
